/**
 * Re-recording from a chosen moment of an Auto-Pace recording.
 *
 * The exported video is assembled from [startMs, endMs] ranges ("chunks") of the raw recording, so
 * a stretch is removed simply by no chunk covering it. Given the chunks closed so far, the chunk
 * that was still open when the recording was paused, and the chosen moment `t`, this returns the
 * chunks that remain: everything before `t`, nothing from `t` on. The re-take is described by a
 * new chunk the caller opens at the recording's current position, never at `t`.
 */
export interface RevertChunk {
    index: number;
    startMs: number;
    endMs: number;
    speed: number;
    kind: 'speaking' | 'silent';
    thumbnail?: string;
    checking?: boolean;
    checks?: { startMs: number }[];
}

export function chunksAfterRevert<C extends RevertChunk>(
    closed: C[],
    open: { startMs: number; speed: number; kind: 'speaking' | 'silent'; thumbnail?: string },
    t: number,
): C[] {
    let kept: C[] = closed
        .filter(c => c.startMs < t)
        .map(c => (c.endMs <= t
            ? c
            : { ...c, endMs: t, checking: false, checks: (c.checks || []).filter(k => k.startMs < t) }));
    if (open.startMs < t) {
        kept = [...kept, { index: kept.length, startMs: open.startMs, endMs: t, speed: open.speed, kind: open.kind, thumbnail: open.thumbnail } as C];
    }
    return kept.filter(c => c.endMs - c.startMs > 0).map((c, i) => ({ ...c, index: i }));
}
